import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { rows: { type: Number, default: 1 } }

  connect() {
    this.squares = []
    this.timeouts = new Set()
    this.gap = 4
    this.size = 6
    this.step = this.size + this.gap

    this.updateColors()
    this.buildGrid()
    this.startFlicker()

    this.resizeHandler = this.buildGrid.bind(this)
    window.addEventListener("resize", this.resizeHandler)

    this.themeHandler = () => { this.updateColors(); this.recolorAll() }
    window.addEventListener("themeChanged", this.themeHandler)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    window.removeEventListener("themeChanged", this.themeHandler)
    if (this.flickerInterval) clearInterval(this.flickerInterval)
    this.timeouts.forEach(t => clearTimeout(t))
  }

  get isDark() {
    return document.documentElement.classList.contains("dark")
  }

  updateColors() {
    if (this.isDark) {
      this.baseColor = "rgba(255, 255, 255, 0.06)"
      this.accentColor = "rgba(229, 176, 112, 0.5)"
      this.brightColor = "rgba(255, 255, 255, 0.3)"
      this.mediumColor = "rgba(255, 255, 255, 0.15)"
    } else {
      this.baseColor = "rgba(0, 0, 0, 0.06)"
      this.accentColor = "rgba(229, 176, 112, 0.4)"
      this.brightColor = "rgba(0, 0, 0, 0.15)"
      this.mediumColor = "rgba(0, 0, 0, 0.08)"
    }
  }

  recolorAll() {
    // Clear all pending fade-back timeouts so old colors don't bleed through
    this.timeouts.forEach(t => clearTimeout(t))
    this.timeouts.clear()

    // Instant recolor — kill transition, set base color, restore transition
    this.squares.forEach(sq => {
      sq.style.transition = "none"
      sq.style.backgroundColor = this.baseColor
    })
    // Force reflow then restore transitions
    void this.element.offsetHeight
    requestAnimationFrame(() => {
      this.squares.forEach(sq => {
        sq.style.transition = "background-color 0.6s ease-in"
      })
    })
  }

  buildGrid() {
    const w = this.element.getBoundingClientRect().width
    this.element.innerHTML = ""
    this.squares = []

    const rowCount = this.rowsValue
    const cols = Math.floor(w / this.step)

    if (rowCount === 1) {
      // Simple single-row mode
      this.element.style.display = "flex"
      this.element.style.flexDirection = ""
      this.element.style.justifyContent = "center"
      this.element.style.gap = `${this.gap}px`
      this.element.style.alignItems = ""

      for (let i = 0; i < cols; i++) {
        const sq = this._makeSquare()
        this.element.appendChild(sq)
        this.squares.push(sq)
      }
      return
    }

    // Multi-row "skyline" mode — columns with varying heights, bottom-aligned
    const heights = this._generateSkyline(cols, rowCount)

    this.element.style.display = "flex"
    this.element.style.flexDirection = ""
    this.element.style.justifyContent = "center"
    this.element.style.alignItems = "flex-end"
    this.element.style.gap = `${this.gap}px`

    for (let c = 0; c < cols; c++) {
      const colEl = document.createElement("div")
      colEl.style.display = "flex"
      colEl.style.flexDirection = "column"
      colEl.style.gap = `${this.gap}px`

      const h = heights[c]
      for (let r = 0; r < h; r++) {
        const sq = this._makeSquare()
        colEl.appendChild(sq)
        this.squares.push(sq)
      }

      this.element.appendChild(colEl)
    }
  }

  _generateSkyline(cols, maxRows) {
    const heights = new Array(cols)
    for (let i = 0; i < cols; i++) {
      heights[i] = 2
    }

    const numBuildings = Math.floor(cols / 8) + 3
    for (let b = 0; b < numBuildings; b++) {
      const center = Math.floor(Math.random() * cols)
      const buildingHeight = 2 + Math.floor(Math.random() * (maxRows - 1))
      const buildingWidth = 2 + Math.floor(Math.random() * 6)

      for (let i = -buildingWidth; i <= buildingWidth; i++) {
        const col = center + i
        if (col < 0 || col >= cols) continue
        const dist = Math.abs(i)
        const taper = Math.max(1, buildingHeight - Math.floor(dist * 0.7))
        heights[col] = Math.max(heights[col], Math.min(taper, maxRows))
      }
    }

    return heights
  }

  _makeSquare() {
    const sq = document.createElement("div")
    sq.style.width = `${this.size}px`
    sq.style.height = `${this.size}px`
    sq.style.borderRadius = "1px"
    sq.style.flexShrink = "0"
    sq.style.backgroundColor = this.baseColor
    sq.style.transition = "background-color 0.6s ease-in"
    return sq
  }

  startFlicker() {
    this.flickerBatch()
    this.flickerInterval = setInterval(() => {
      this.flickerBatch()
    }, 500)
  }

  flickerBatch() {
    const total = this.squares.length
    if (total === 0) return

    const count = Math.max(1, Math.floor(total * 0.06))

    for (let i = 0; i < count; i++) {
      const idx = Math.floor(Math.random() * total)
      const sq = this.squares[idx]
      const rand = Math.random()

      sq.style.transition = "background-color 0.15s ease-out"

      if (rand < 0.3) {
        sq.style.backgroundColor = this.accentColor
      } else if (rand < 0.6) {
        sq.style.backgroundColor = this.brightColor
      } else {
        sq.style.backgroundColor = this.mediumColor
      }

      const fadeDelay = 500 + Math.random() * 1000
      const t = setTimeout(() => {
        sq.style.transition = "background-color 1.5s ease-in"
        sq.style.backgroundColor = this.baseColor
        this.timeouts.delete(t)
      }, fadeDelay)
      this.timeouts.add(t)
    }
  }
}
